import React from 'react'

import { Cell, Line, Terminal } from '../interface'
import { GameGenerator, GameState } from '../services/gameGenerator'

export const CONTAINER_HEIGHT = 420
export const CONTAINER_WIDTH = 420

export interface ContainerProps {
    initialRowNumber: number
    initialColumnNumber: number
}

export const useContainer = (containerProps: ContainerProps) => {
    const [isMouseDown, setIsMouseDown] = React.useState(false)
    const [previousCell, setPreviousCell] = React.useState<Cell | undefined>(undefined)
    const [toggleAnswer, setToggleAnswer] = React.useState(false)

    const [dialogTitle, setDialogTitle] = React.useState('')
    const [dialogContent, setDialogContent] = React.useState('')
    const [dialogOpened, setDialogOpened] = React.useState(false)
    const [dialogAction, setDialogAction] = React.useState<(() => () => void) | undefined>(undefined)

    const gameGenerator = React.useCallback((rowNumber: number, columnNumber: number) => {
        const gameGenerator = new GameGenerator()
        gameGenerator.setRowNumber(rowNumber)
        gameGenerator.setColumnNumber(columnNumber)
        return gameGenerator.generate()
    }, [])

    const generateNewGame = React.useCallback((rowNumber: number, columnNumber: number) =>
        setGameState(gameGenerator(rowNumber, columnNumber)), [gameGenerator])

    const { initialRowNumber, initialColumnNumber } = containerProps
    const initialGameState = React.useMemo(() =>
        gameGenerator(initialRowNumber, initialColumnNumber),
        [gameGenerator, initialRowNumber, initialColumnNumber])
    const [gameState, setGameState] = React.useState<GameState>(initialGameState)
    const { grid, terminals, lines, rowNumber, columnNumber } = gameState

    const getCellByPosition = React.useCallback((x: number, y: number, rect: DOMRect): Cell => {
        const heightPerRow = rect.height / rowNumber
        const widthPerColumn = rect.width / columnNumber

        y -= rect.top
        x -= rect.left
        let row = Math.floor(y / heightPerRow)
        let column = Math.floor(x / widthPerColumn)

        if (column < 0) column = 0
        if (row < 0) row = 0

        return { row, column }
    }, [rowNumber, columnNumber])

    const terminalCellDict: Array<Array<Terminal | undefined>> = React.useMemo(() => {
        const result: Array<Array<Terminal | undefined>> = []
        for (let i = 0; i < rowNumber; i++) {
            result.push([])
            for (let j = 0; j < columnNumber; j++)
                result[i].push(undefined)
        }

        for (let i = 0; i < terminals.length; i++) {
            const { row, column } = terminals[i].cell
            result[row][column] = terminals[i]
        }
        return result
    }, [terminals, rowNumber, columnNumber])

    const lineSourceCellDict: Array<Array<Array<Line>>> = React.useMemo(() => {
        const result: Array<Array<Array<Line>>> = []
        for (let i = 0; i < rowNumber; i++) {
            result.push([])
            for (let j = 0; j < columnNumber; j++)
                result[i].push([])
        }

        for (let i = 0; i < lines.length; i++) {
            const { row, column } = lines[i].source
            result[row][column].push(lines[i])
        }
        return result
    }, [lines, rowNumber, columnNumber])

    const lineDestinationCellDict: Array<Array<Array<Line>>> = React.useMemo(() => {
        const result: Array<Array<Array<Line>>> = []
        for (let i = 0; i < rowNumber; i++) {
            result.push([])
            for (let j = 0; j < columnNumber; j++)
                result[i].push([])
        }

        for (let i = 0; i < lines.length; i++) {
            const { row, column } = lines[i].destination
            result[row][column].push(lines[i])
        }
        return result
    }, [lines, rowNumber, columnNumber])

    const isValidLine = React.useCallback((source: Cell, destination: Cell): boolean => {
        if (source.row < 0 || source.row >= rowNumber) return false
        if (destination.row < 0 || destination.row >= rowNumber) return false
        if (source.column < 0 || source.column >= columnNumber) return false
        if (destination.column < 0 || destination.column >= columnNumber) return false

        return (Math.abs(source.row - destination.row) +
            Math.abs(source.column - destination.column)) === 1
    }, [rowNumber, columnNumber])

    const isValidSourceCell = React.useCallback((cell: Cell, color: string): boolean => {
        const lineSourceCell = lineSourceCellDict[cell.row][cell.column]
        if (lineSourceCell.length) return false

        const lineDestinationCell = lineDestinationCellDict[cell.row][cell.column]
        if (terminalCellDict[cell.row][cell.column]) {
            if (lineDestinationCell.length) return false
        } else {
            if (lineDestinationCell.length === 0) return false
            if (lineDestinationCell.length === 2) return false
            if (lineDestinationCell[0].color !== color) return false
        }

        return true
    }, [terminalCellDict, lineSourceCellDict, lineDestinationCellDict])

    const isValidDestinationCell = React.useCallback((cell: Cell, color: string): boolean => {
        const lineSourceCell = lineSourceCellDict[cell.row][cell.column]
        if (lineSourceCell.length) return false

        const lineDestinationCell = lineDestinationCellDict[cell.row][cell.column]
        if (lineDestinationCell.length && (lineDestinationCell[0].color !== color)) {
            return false
        }

        const terminalCell = terminalCellDict[cell.row][cell.column]
        if (terminalCell && (terminalCell.color !== color)) {
            return false
        }

        return true
    }, [terminalCellDict, lineSourceCellDict, lineDestinationCellDict])

    const isBackwardMove = React.useCallback((source: Cell, destination: Cell): boolean => {
        const possibleLines = lineSourceCellDict[destination.row][destination.column]
        for (let i = 0; i < possibleLines.length; i++) {
            if ((possibleLines[i].source.row === destination.row) &&
                (possibleLines[i].source.column === destination.column) &&
                (possibleLines[i].destination.row === source.row) &&
                (possibleLines[i].destination.column === source.column))
                return true
        }
        return false
    }, [lineSourceCellDict])

    const resetConnectAction = React.useCallback(() => {
        setIsMouseDown(false)
        setPreviousCell(undefined)
    }, [])

    const onMouseDown = React.useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault()
        if (toggleAnswer) return
        if (!isMouseDown) {
            setIsMouseDown(true)
            const rect = e.currentTarget.getBoundingClientRect()
            const x = e.clientX
            const y = e.clientY
            const cell = getCellByPosition(x, y, rect)
            const terminalCell = terminalCellDict[cell.row][cell.column]
            if (terminalCell) {
                setGameState((previousGameState) => ({
                    ...previousGameState,
                    lines: previousGameState.lines
                        .filter((line) => line.color !== terminalCell.color)
                }))
            }
        }
    }, [isMouseDown, toggleAnswer, terminalCellDict, getCellByPosition])

    const onTouchStart = React.useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        //e.preventDefault()
        if (toggleAnswer) return

        if (!isMouseDown) {
            setIsMouseDown(true)
            const rect = e.currentTarget.getBoundingClientRect()
            const touch = e.touches[0]
            const x = touch.clientX
            const y = touch.clientY
            const cell = getCellByPosition(x, y, rect)
            const terminalCell = terminalCellDict[cell.row][cell.column]
            if (terminalCell) {
                setGameState((previousGameState) => ({
                    ...previousGameState,
                    lines: previousGameState.lines
                        .filter((line) => line.color !== terminalCell.color)
                }))
            }
        }
    }, [isMouseDown, toggleAnswer, terminalCellDict, getCellByPosition])

    const onMouseUp = React.useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault()
        if (isMouseDown) resetConnectAction()
    }, [isMouseDown, resetConnectAction])

    const onTouchEnd = React.useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        //e.preventDefault()
        if (isMouseDown) resetConnectAction()
    }, [isMouseDown, resetConnectAction])

    const drawLine = React.useCallback((source: Cell | undefined, destination: Cell) => {
        if (!source) {
            setPreviousCell(destination)
        } else if (isValidLine(source, destination)) {
            if (isBackwardMove(source, destination)) {
                setGameState((previousGameState) => ({
                    ...previousGameState,
                    lines: previousGameState.lines.filter((line: Line) =>
                        !((line.source.row === destination.row) &&
                            (line.source.column === destination.column) &&
                            (line.destination.row === source.row) &&
                            (line.destination.column === source.column)))
                }))
                setPreviousCell(destination)
            } else {
                const line = lineDestinationCellDict[source.row][source.column]
                const terminal = terminalCellDict[source.row][source.column]
                const color = line[0]?.color || terminal?.color

                if (color && isValidSourceCell(source, color) &&
                    isValidDestinationCell(destination, color)) {
                    setGameState((previousGameState) => ({
                        ...previousGameState,
                        lines: [...previousGameState.lines, {
                            source: source,
                            destination: destination,
                            color: color
                        }]
                    }))

                    if (terminalCellDict[destination.row][destination.column]) {
                        resetConnectAction()
                    } else {
                        setPreviousCell(destination)
                    }
                }
            }
        }
    }, [terminalCellDict, lineDestinationCellDict, isBackwardMove, isValidLine,
        isValidSourceCell, isValidDestinationCell, resetConnectAction])

    const onMouseMove = React.useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault()
        if (isMouseDown) {
            const rect = e.currentTarget.getBoundingClientRect()
            const x = e.clientX
            const y = e.clientY

            const cell = getCellByPosition(x, y, rect)
            if ((cell.row !== previousCell?.row) ||
                (cell.column !== previousCell?.column)) {
                drawLine(previousCell, cell)
            }
        }
    }, [getCellByPosition, drawLine, isMouseDown, previousCell])

    const onTouchMove = React.useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        //e.preventDefault()
        if (isMouseDown) {
            const rect = e.currentTarget.getBoundingClientRect()
            const touch = e.touches[0]
            const x = touch.clientX
            const y = touch.clientY

            const cell = getCellByPosition(x, y, rect)

            if ((cell.row !== previousCell?.row) ||
                (cell.column !== previousCell?.column)) {
                drawLine(previousCell, cell)
            }
        }
    }, [getCellByPosition, drawLine, isMouseDown, previousCell])

    const onMouseLeave = React.useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault()
        if (isMouseDown) resetConnectAction()
    }, [isMouseDown, resetConnectAction])

    const handleToggleAnswer = React.useCallback(() =>
        setToggleAnswer((previousToggleAnswer) => {
            if (!previousToggleAnswer) {
                setGameState((previousGameState) => ({
                    ...previousGameState,
                    temporaryLines: [...previousGameState.lines],
                    lines: [...previousGameState.answerLines]
                }))
            } else {
                setGameState((previousGameState) => ({
                    ...previousGameState,
                    lines: [...previousGameState.temporaryLines],
                    temporaryLines: []
                }))
            }
            return !previousToggleAnswer
        }), [])


    const handleResetGame = React.useCallback(() => setGameState((previousGameState) => ({
        ...previousGameState,
        lines: [],
        temporaryLines: []
    })), [])

    const openDialog = React.useCallback((title: string, content: string, action: (() => () => void)) => {
        setDialogTitle(title)
        setDialogContent(content)
        setDialogAction(() => action)
        setDialogOpened(true)
    }, [])

    const openDialogForResetGame = React.useCallback(() =>
        openDialog('Reset Game', 'Are you sure want to reset current game?',
            () => handleResetGame), [openDialog, handleResetGame])

    const openDialogForChangeGridSize =
        React.useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
            openDialog('New Game', 'Are you sure want to start new game',
                () => () => {
                    const value = event.target.value as number
                    generateNewGame(value, value)
                })
        }, [openDialog, generateNewGame])

    const openDialogForNewGame = React.useCallback(() =>
        openDialog('New Game', 'Are you sure want to start new game?',
            () => () => generateNewGame(rowNumber, columnNumber)),
        [openDialog, generateNewGame, rowNumber, columnNumber])

    const openDialogForWinningGame = React.useCallback(() =>
        openDialog('Congratulations!', `You have solve this puzzle, 
            Are you want to start new game?'`,
            () => () => generateNewGame(rowNumber, columnNumber)),
        [openDialog, generateNewGame, rowNumber, columnNumber])

    const handleDialogYesButton = React.useCallback(() => {
        dialogAction?.()()
        setDialogOpened(false)
    }, [dialogAction])

    const handleDialogNoButton = React.useCallback(() => setDialogOpened(false), [])

    React.useEffect(() => {
        if ((lines.length === (rowNumber * columnNumber - (terminals.length / 2))) &&
            !toggleAnswer) {
            openDialogForWinningGame()
        }
    }, [lines.length])

    return {
        state: {
            grid, rowNumber, columnNumber,
            terminalCellDict, lineSourceCellDict,
            lineDestinationCellDict, toggleAnswer,
            dialogTitle, dialogContent, dialogOpened
        },
        operation: {
            onMouseDown, onMouseUp, onMouseMove,
            onTouchStart, onTouchMove, onTouchEnd,
            onMouseLeave, handleToggleAnswer, openDialogForChangeGridSize,
            openDialogForResetGame, openDialogForNewGame,
            handleDialogYesButton, handleDialogNoButton
        }
    }
}
